import React, { useEffect, useMemo, useState } from 'react';
import { CustomPagination } from '~/shared/ui/tables/CustomPagination';
import {
    DataGrid,
    GridPaginationModel,
    GridRowSelectionModel,
} from '@mui/x-data-grid';
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { FilterBar } from './FilterBar';
import { IDataTableProps } from '~/shared/types/types';

export const DataTable: React.FC<IDataTableProps> = ({
    rows,
    columns,
    rowCount,
    isFetching,
    error,
    selectedIds,
    setSelectedIds,
    paginationModel,
    setPaginationModel,
    striped = true,
    filterColumns = [],
    onFilterApply,
    onFilterReset,
    filterDefaultState,
    sortModel,
    setSortModel,
    sortingMode = 'client',
    loadingText,
    loading,
    onRefresh,
    getRowHeight,
    checkboxSelection = true,
    activeFilters,
}) => {
    const [isFilterBarOpen, setIsFilterBarOpen] = useState(!!activeFilters);
    const theme = useTheme();
    const isRefreshing = Boolean(isFetching || loading);

    const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
        const visibleIds = rows.map(r => r.id);
        setSelectedIds((prev: any) => {
            const added = newSelection.filter(id => !prev.includes(id));
            const removed = visibleIds.filter(id => !newSelection.includes(id));
            return [...prev.filter((id: number) => !removed.includes(id)), ...added];
        });
    };

    useEffect(() => {
        if (error && paginationModel.search) {
            setPaginationModel((prev: any) => ({ ...prev, search: '' }));
        }
    }, [error, setPaginationModel, paginationModel.search]);

    const processedColumns = useMemo(
        () =>
            columns.map((col) => {
                if (col.field === 'id' || col.field === 'actions' || col.field === 'checkbox') {
                    return col;
                }
                if (col.flex != null) {
                    return col;
                }
                return { ...col, flex: 1 };
            }),
        [columns],
    );

    return (
        <Box>
            {filterColumns.length > 0 && (
                <FilterBar
                    open={isFilterBarOpen}
                    toggleFilterBar={setIsFilterBarOpen}
                    filterColumns={filterColumns}
                    onFilterApply={onFilterApply}
                    onFilterReset={onFilterReset}
                    defaultState={filterDefaultState}
                    activeFilters={activeFilters}
                />
            )}

            <DataGrid
                checkboxSelection={checkboxSelection}
                disableRowSelectionOnClick
                rows={rows}
                columns={processedColumns}
                rowCount={rowCount}
                loading={isFetching || loading}
                sortingMode={sortingMode}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                paginationMode="server"
                paginationModel={paginationModel as GridPaginationModel}
                onPaginationModelChange={setPaginationModel as any}
                rowSelectionModel={selectedIds}
                onRowSelectionModelChange={handleSelectionChange}
                getRowHeight={getRowHeight}
                getRowClassName={params =>
                    striped && params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : ''
                }
                density="compact"
                sx={{
                    maxHeight: 'calc(100vh - 200px)',
                    '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'center',
                    },
                }}
                slots={{
                    loadingOverlay: () => (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 1,
                                height: '100%',
                            }}
                        >
                            <CircularProgress size={28} />
                            <Typography variant="body2">{loadingText || 'Идет поиск...'}</Typography>
                        </Box>
                    ),
                    footer: () => (
                        <CustomPagination
                            page={paginationModel.page}
                            pageSize={paginationModel.pageSize}
                            search={paginationModel.search}
                            rowCount={rowCount}
                            onSearchChange={(newSearch) =>
                                setPaginationModel((prev: any) => ({ ...prev, search: newSearch }))
                            }
                            onPageChange={(newPage) =>
                                setPaginationModel((prev: any) => ({ ...prev, page: newPage }))
                            }
                            onPageSizeChange={(newPageSize) => {
                                localStorage.setItem('page_size', String(newPageSize));
                                setPaginationModel((prev: any) => ({
                                    ...prev,
                                    page: 0,
                                    pageSize: newPageSize,
                                }));
                            }}
                        />
                    ),
                    toolbar: () => (
                        <Stack sx={{ backgroundColor: theme.palette.background.paper }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                px={2}
                                py={1}
                                sx={{
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="body2">{selectedIds.length} выбрано</Typography>
                                <Stack direction="row" gap={1}>
                                    {filterColumns.length > 0 && (
                                        <Button
                                            size="small"
                                            onClick={() => setIsFilterBarOpen(prev => !prev)}
                                            variant="outlined"
                                            startIcon={<FilterAltOutlinedIcon />}
                                        >
                                            {isFilterBarOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
                                        </Button>
                                    )}
                                    {onRefresh && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={onRefresh}
                                            disabled={isRefreshing}
                                            startIcon={<RefreshRoundedIcon />}
                                        >
                                            Обновить
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => setSelectedIds([])}
                                        variant="outlined"
                                        disabled={selectedIds.length === 0}
                                        size="small"
                                        startIcon={<RestartAltIcon />}
                                    >
                                        Сбросить выбор
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    ),
                }}
                slotProps={{
                    filterPanel: {
                        filterFormProps: {
                            logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                            columnInputProps: {
                                variant: 'outlined',
                                size: 'small',
                                sx: { mt: 'auto' },
                            },
                            operatorInputProps: {
                                variant: 'outlined',
                                size: 'small',
                                sx: { mt: 'auto' },
                            },
                            valueInputProps: {
                                InputComponentProps: { variant: 'outlined', size: 'small' },
                            },
                        },
                    },
                }}
            />
        </Box>
    );
};
